import { useEffect, useState } from "react";
import { Loader2, Printer, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { ETIQUETAS_PADRAO, type ConfigDeEtiquetas } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { trpc } from "@/lib/trpc";
import { imprimirPagina, paginaDeEntrega, paginaDeProducao } from "@/components/production/etiquetas";
import { paginaUnica } from "@/components/production/etiqueta-unica";
import { ControlesDaEtiquetaUnica } from "./ControlesDaEtiquetaUnica";
import { ControlesDaEtiqueta } from "./ControlesDaEtiqueta";
import { PreviaDaEtiqueta } from "./PreviaDaEtiqueta";
import { DESTINO_EXEMPLO, PEDIDO_EXEMPLO, REMETENTE_EXEMPLO, SERVICOS_EXEMPLO } from "./exemplo";
import { useLinkDoCatalogo } from "@/components/catalogo/publico/use-link-do-catalogo";

type Aba = "producao" | "entrega" | "unica";

const botao =
  "inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-[12.5px] font-semibold text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-muted";

/** Configurações › Etiquetas: como saem as etiquetas de produção e de entrega deste laboratório. */
export function EtiquetasSecao() {
  const utils = trpc.useUtils();
  const salvo = trpc.organization.etiquetas.useQuery();
  const perfil = trpc.organization.getProfiles.useQuery();
  const [rascunho, setRascunho] = useState<ConfigDeEtiquetas>(ETIQUETAS_PADRAO);
  const [aba, setAba] = useState<Aba>("producao");

  useEffect(() => {
    if (salvo.data) setRascunho(salvo.data);
  }, [salvo.data]);

  const gravar = trpc.organization.setEtiquetas.useMutation({
    onSuccess: (config, entrada) => {
      toast.success(entrada === null ? "Etiquetas de volta ao padrão." : "Etiquetas salvas.");
      utils.organization.etiquetas.setData(undefined, config);
    },
    onError: (e) => toast.error(e.message || "Não foi possível salvar."),
  });

  const nome = perfil.data?.labProfile?.name || perfil.data?.name || "Laboratório";
  const logo = perfil.data?.labProfile?.logoUrl ?? perfil.data?.logoUrl ?? null;
  const urlDoCatalogo = useLinkDoCatalogo();
  const pagina =
    aba === "producao"
      ? paginaDeProducao(rascunho.producao, PEDIDO_EXEMPLO, SERVICOS_EXEMPLO, logo)
      : aba === "entrega"
        ? paginaDeEntrega(rascunho.entrega, PEDIDO_EXEMPLO, SERVICOS_EXEMPLO, DESTINO_EXEMPLO, REMETENTE_EXEMPLO(nome), logo, urlDoCatalogo)
        : paginaUnica(rascunho.unica, PEDIDO_EXEMPLO, SERVICOS_EXEMPLO, DESTINO_EXEMPLO, `${window.location.origin}/lab/pedidos`, logo, urlDoCatalogo);
  const alterado = JSON.stringify(rascunho) !== JSON.stringify(salvo.data ?? ETIQUETAS_PADRAO);

  if (salvo.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
      </div>
    );
  }

  return (
    <div className="space-y-5 px-6 py-5">
      <div className="flex gap-1 rounded-lg border border-zinc-200 bg-zinc-50 p-1 sm:w-fit dark:border-border dark:bg-muted/40">
        {(
          [
            ["producao", "Produção (uma por serviço)"],
            ["entrega", "Entrega (uma por pedido)"],
            ["unica", "Única (produção + expedição)"],
          ] as const
        ).map(([chave, rotulo]) => (
          <button
            key={chave}
            type="button"
            onClick={() => setAba(chave)}
            className={cn(
              "h-8 flex-1 rounded-md px-3 text-xs font-semibold transition-colors sm:flex-none",
              aba === chave ? "bg-brand-600 text-white shadow-sm" : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground",
            )}
          >
            {rotulo}
          </button>
        ))}
      </div>

      <div className={cn("grid gap-6", aba === "unica" ? "lg:grid-cols-[minmax(0,1fr)_420px] lg:items-start" : "lg:grid-cols-[1fr_minmax(0,320px)]")}>
        {aba === "producao" ? (
          <ControlesDaEtiqueta tipo="producao" config={rascunho.producao} onChange={(producao) => setRascunho({ ...rascunho, producao })} />
        ) : aba === "entrega" ? (
          <ControlesDaEtiqueta tipo="entrega" config={rascunho.entrega} onChange={(entrega) => setRascunho({ ...rascunho, entrega })} />
        ) : (
          <ControlesDaEtiquetaUnica config={rascunho.unica} onChange={(unica) => setRascunho({ ...rascunho, unica })} />
        )}
        <PreviaDaEtiqueta
          pagina={pagina}
          a4={aba !== "unica" && (aba === "producao" ? rascunho.producao : rascunho.entrega).tamanho === "a4"}
          altura={aba === "unica" ? Math.round(rascunho.unica.altura * 3.78) + 40 : undefined}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-zinc-100 pt-4 dark:border-border">
        <div className="flex flex-wrap gap-2">
          <button type="button" className={botao} onClick={() => imprimirPagina("Etiqueta de teste", pagina) || toast.error("O navegador bloqueou a janela de impressão.")}>
            <Printer className="h-3.5 w-3.5" />
            Imprimir teste
          </button>
          <button type="button" className={botao} disabled={gravar.isPending} onClick={() => gravar.mutate(null)}>
            <RotateCcw className="h-3.5 w-3.5" />
            Restaurar padrão
          </button>
        </div>
        <button
          type="button"
          disabled={!alterado || gravar.isPending}
          onClick={() => gravar.mutate(rascunho)}
          className="inline-flex h-9 items-center gap-1.5 rounded-md bg-brand-600 px-4 text-[12.5px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-50"
        >
          {gravar.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Salvar
        </button>
      </div>
    </div>
  );
}
